import { useEffect, type RefObject } from 'react';

import { MOTION } from '../design/tokens';

/** Marks an element as revealable. Queried by the observer below. */
export const REVEAL_ATTRIBUTE = 'data-reveal';

/** Set once an element has entered view. Drives the CSS transition. */
export const REVEALED_ATTRIBUTE = 'data-revealed';

export interface RevealOptions {
  /**
   * The scroll container above the split breakpoint, or null when the document scrolls.
   *
   * This is the single most common way to get this layout wrong. Above 860px the right
   * pane scrolls, not the window, so an observer rooted at the viewport sees every
   * section as permanently intersecting and fires them all at once.
   */
  readonly root: Element | null;

  /** When true, everything is revealed immediately and no observer is created. */
  readonly disabled: boolean;
}

/**
 * Reveals elements as they scroll into view, staggered within a list.
 *
 * **Elements start VISIBLE and are hidden by this hook**, not by CSS. That inversion is
 * deliberate: `opacity: 0` in the stylesheet means a failure to run — a dead observer, a
 * thrown effect, JavaScript disabled — leaves a permanently blank page. The prototype
 * has that exposure. Here, the worst case is that content simply appears without
 * animating.
 */
export function useReveal(
  containerRef: RefObject<HTMLElement | null>,
  { root, disabled }: RevealOptions,
): void {
  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const targets = Array.from(
      container.querySelectorAll<HTMLElement>(`[${REVEAL_ATTRIBUTE}]`),
    );

    if (disabled || typeof IntersectionObserver === 'undefined') {
      // Reduced motion, or no observer support: resolve straight to the final state.
      // § UI/UX requires reveals to resolve, never to be skipped into invisibility.
      targets.forEach((target) => {
        target.setAttribute(REVEALED_ATTRIBUTE, 'true');
      });
      return;
    }

    // Hide only what we are about to animate, and only now that we know the observer
    // exists and the effect has run.
    targets.forEach((target) => {
      if (!target.hasAttribute(REVEALED_ATTRIBUTE)) {
        target.setAttribute(REVEALED_ATTRIBUTE, 'false');
      }
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries
          .filter((entry) => entry.isIntersecting)
          .forEach((entry) => {
            const target = entry.target as HTMLElement;
            const group = target.closest(`[${REVEAL_ATTRIBUTE}-group]`);

            // Stagger within a list, capped so a long list does not leave its tail
            // invisible for seconds.
            const index = group
              ? Array.from(group.querySelectorAll(`[${REVEAL_ATTRIBUTE}]`)).indexOf(
                  target,
                )
              : 0;

            const step = Math.min(Math.max(index, 0), MOTION.revealStaggerCap);

            target.style.setProperty(
              '--reveal-delay',
              `${step * MOTION.revealStaggerMs}ms`,
            );
            target.setAttribute(REVEALED_ATTRIBUTE, 'true');

            observer.unobserve(target);
          });
      },
      {
        root,
        threshold: 0.08,
        rootMargin: '0px 0px -4% 0px',
      },
    );

    targets
      .filter((target) => target.getAttribute(REVEALED_ATTRIBUTE) !== 'true')
      .forEach((target) => {
        observer.observe(target);
      });

    return () => {
      observer.disconnect();
    };

    // `root` is in the dependency list on purpose: crossing the split breakpoint changes
    // which element scrolls, so the observer must be rebuilt rather than left pointing at
    // a container that no longer scrolls.
  }, [containerRef, root, disabled]);
}
